import { useState, type Dispatch, type SetStateAction } from "react";

import {
  usePick,
  useTaxonomy,
  type PickBucket,
  type PickItem,
  type PickQuestion,
  type TaxonomyEntryOut,
} from "@/api/queries";
import { Button } from "@/components/ui/button";
import { useJDs } from "@/api/jd-queries";
import { MixLine, SourceChip } from "@/components/jd-source-chip";
import { FieldLabel, Input, Label, Select } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, PaneHeader, Panel, PanelHeader } from "@/components/ui/panel";
import { cn } from "@/lib/utils";

/** §17.4 — the order a set is asked in. Project domain first; the Interviewer prepends its
 * own opener, which is what told it which domain to ask for. */
const BUCKETS = ["project_domain", "technical", "problem_solving", "soft_skill"] as const;
type Bucket = (typeof BUCKETS)[number];

const DEFAULTS: Record<Bucket, number> = {
  project_domain: 3,
  technical: 6,
  problem_solving: 2,
  soft_skill: 2,
};

/** Which taxonomy stacks belong to a bucket — the same derivation the picker does server
 * side (§17.2), so the dropdowns only offer what that bucket can actually serve. */
function stacksFor(entries: TaxonomyEntryOut[] | undefined, bucket: Bucket) {
  return (entries ?? []).filter((e) => e.bucket === bucket).map((e) => e.path);
}

/** A multi-select as toggle chips. Combobox is single-value, and §17.3 asks for stacks[]
 * and domains[] — the same facet-row shape the Questions filter bar uses. */
function ChipRow({
  options,
  selected,
  onToggle,
  empty,
  disabled = [],
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  empty: string;
  /** Chips that cannot be picked here, e.g. a stack already chosen in the other tier (§17.12). */
  disabled?: string[];
}) {
  if (options.length === 0) return <p className="text-bank-muted py-1 text-xs">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o);
        const off = disabled.includes(o);
        return (
          <button
            key={o}
            type="button"
            aria-pressed={on}
            disabled={off}
            title={off ? "Already chosen in the other list" : undefined}
            onClick={() => onToggle(o)}
            className={cn(
              "rounded border px-2 py-1 font-mono text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-40",
              on
                ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink"
                : "border-bank-border text-bank-muted hover:border-bank-border-strong",
            )}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function QuestionBlock({ q, follow }: { q: PickQuestion; follow?: boolean }) {
  return (
    <div className={cn(follow && "border-bank-border ml-4 border-l pl-3")}>
      <p className="text-sm">{q.question_text}</p>
      <div className="text-bank-muted mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]">
        <span>#{q.question_id}</span>
        <span>d{q.difficulty_score ?? "—"}</span>
        <span>{q.stack_path ?? "—"}</span>
        {q.primary_concept && <span>{q.primary_concept}</span>}
        {q.question_type && <span>{q.question_type}</span>}
      </div>
      {q.model_answer && (
        <details className="mt-1.5">
          <summary className="text-bank-muted cursor-pointer text-xs">model answer &amp; rubric</summary>
          <p className="border-bank-border bg-bank-surface-2 mt-1.5 rounded-md border px-2.5 py-2 text-xs">
            {q.model_answer}
          </p>
          {q.rubric && q.rubric.length > 0 && (
            <ul className="text-bank-muted mt-1.5 list-disc pl-5 text-xs">
              {q.rubric.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </details>
      )}
    </div>
  );
}

function BucketPanel({ bucket }: { bucket: PickBucket }) {
  return (
    <Panel>
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{bucket.bucket}</span>
            <span className="text-bank-muted text-xs">
              {bucket.returned} of {bucket.requested}
            </span>
          </span>
        }
        actions={bucket.mix && <MixLine mix={bucket.mix} />}
      />
      <div className="space-y-3 p-4">
        {/* §17.7 — a short set must never be silent. */}
        {bucket.relaxed && (
          <Callout tone="attention">
            Relaxed: the filter had to widen, or the bank could not fill this bucket. Returned{" "}
            {bucket.returned} of {bucket.requested}.
          </Callout>
        )}
        {bucket.items.length === 0 ? (
          <Empty>No questions in this bucket yet.</Empty>
        ) : (
          bucket.items.map((it: PickItem) => (
            <div key={it.question.question_id} className="space-y-2">
              <div className="flex gap-2">
                <span className="text-bank-muted w-6 shrink-0 pt-0.5 font-mono text-xs">
                  {it.sequence_number}.
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  {/* DESIGN §20.9: with a JD, where each question came from. */}
                  {it.source && <SourceChip source={it.source} />}
                  <QuestionBlock q={it.question} />
                  {it.follow_ups.map((f) => (
                    <QuestionBlock key={f.question_id} q={f} follow />
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

/**
 * A console for Engine 2 — DESIGN §17. This screen is standing in for the external
 * Interviewer engine: it sends exactly the request that engine sends, to the one
 * authenticated route in the app, so what you see here is what that integration gets.
 */
export default function Picker() {
  const [apiKey, setApiKey] = useState("");
  // DESIGN §17.12: primary stacks use the experience band, secondary ones an easier range.
  const [primary, setPrimary] = useState<string[]>([]);
  const [secondary, setSecondary] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [experience, setExperience] = useState("4");
  const [followUps, setFollowUps] = useState("2");
  const [counts, setCounts] = useState<Record<Bucket, number>>(DEFAULTS);
  // DESIGN §20.9: a published JD decides the stacks, so choosing one sends `jd_id` and no stacks.
  const [jdId, setJdId] = useState("");

  const { data: taxonomy } = useTaxonomy("stack");
  const { data: activeJds } = useJDs("active");
  const pick = usePick();

  const technicalStacks = stacksFor(taxonomy, "technical");
  const domainStacks = stacksFor(taxonomy, "project_domain");

  // Functional updater, not `[...stacks, v]`: two toggles in one tick both read the same
  // captured array and the second silently discards the first.
  function toggle(set: Dispatch<SetStateAction<string[]>>, v: string) {
    set((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }

  // A candidate has at least one main skill (§17.12); a JD supplies its own stacks instead.
  const needsPrimary = !jdId && primary.length === 0;

  function run() {
    if (!apiKey.trim() || needsPrimary) return;
    pick.mutate({
      apiKey: apiKey.trim(),
      buckets: Object.fromEntries(
        BUCKETS.map((b) => [b, counts[b]]).filter(([, n]) => (n as number) > 0),
      ) as Record<string, number>,
      primary_stacks: jdId ? [] : primary.map((path) => ({ path, weight: null })),
      secondary_stacks: jdId ? [] : secondary.map((path) => ({ path, weight: null })),
      jd_id: jdId ? Number(jdId) : null,
      domains: domains.map((path) => ({ path, weight: null })),
      experience_years: experience === "" ? null : Number(experience),
      follow_ups_per_technical: followUps === "" ? null : Number(followUps),
      exclude_question_ids: [],
    });
  }

  const result = pick.data;

  return (
    <div className="flex flex-col gap-3 p-4">
      <h1 className="text-base font-semibold tracking-tight">Picker console</h1>
      <Callout>
        Sends the same request the external Interviewer engine sends to <code>POST /pick</code> — the one
        authenticated route here. A pick makes no model call and stores nothing (§17.1).
      </Callout>

      <Panel>
        <PanelHeader title="Request" />
        <div className="space-y-4 p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-56 flex-1">
              <FieldLabel htmlFor="pick-key">Service key (X-API-Key)</FieldLabel>
              <Input
                id="pick-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="service_auth.api_key"
                onKeyDown={(e) => e.key === "Enter" && run()}
              />
            </div>
            <div className="w-28">
              <FieldLabel htmlFor="pick-exp">Experience (yrs)</FieldLabel>
              <Input
                id="pick-exp"
                type="number"
                min={0}
                max={60}
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
              />
            </div>
            <div className="w-32">
              <FieldLabel htmlFor="pick-follow">Follow-ups each</FieldLabel>
              <Input
                id="pick-follow"
                type="number"
                min={0}
                max={5}
                value={followUps}
                onChange={(e) => setFollowUps(e.target.value)}
              />
            </div>
          </div>

          <div>
            <PaneHeader title="Counts per bucket" />
            <div className="mt-2 flex flex-wrap gap-3">
              {BUCKETS.map((b) => (
                <div key={b} className="w-40">
                  <Label htmlFor={`count-${b}`}>{b}</Label>
                  <Input
                    id={`count-${b}`}
                    type="number"
                    min={0}
                    max={50}
                    value={counts[b]}
                    onChange={(e) => setCounts((prev) => ({ ...prev, [b]: Number(e.target.value) }))}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-xl">
            <FieldLabel htmlFor="pick-jd">Job description</FieldLabel>
            <Select id="pick-jd" value={jdId} onChange={(e) => setJdId(e.target.value)}>
              <option value="">None — pick the stacks below</option>
              {(activeJds?.items ?? []).map((jd) => (
                <option key={jd.id} value={jd.id}>
                  {jd.title}
                </option>
              ))}
            </Select>
            <p className="text-bank-muted mt-1 text-xs">
              Only published JDs. The JD then chooses the stacks and their weights, its domains if it names
              any, and the experience the set is pitched at (DESIGN §20.9).
            </p>
          </div>

          <div>
            <PaneHeader title="Primary stacks (required)" />
            <div className="mt-2">
              {jdId ? (
                <p className="text-bank-muted text-xs">
                  Set by the job description:{" "}
                  <span className="font-mono">
                    {(activeJds?.items.find((jd) => String(jd.id) === jdId)?.stacks ?? []).join(" · ") || "—"}
                  </span>
                </p>
              ) : (
                <>
                  <ChipRow
                    options={technicalStacks}
                    selected={primary}
                    disabled={secondary}
                    onToggle={(v) => toggle(setPrimary, v)}
                    empty="No technical stacks in the taxonomy."
                  />
                  <p className="text-bank-muted mt-1.5 text-xs">
                    The candidate's strongest skills — asked at the experience level's usual difficulty, and
                    given the larger share of the technical questions.
                  </p>
                </>
              )}
            </div>
          </div>

          {!jdId && (
            <div>
              <PaneHeader title="Secondary stacks (optional)" />
              <div className="mt-2">
                <ChipRow
                  options={technicalStacks}
                  selected={secondary}
                  disabled={primary}
                  onToggle={(v) => toggle(setSecondary, v)}
                  empty="No technical stacks in the taxonomy."
                />
                <p className="text-bank-muted mt-1.5 text-xs">
                  Weaker skills — asked an easier range, set per experience level on the Config page (DESIGN
                  §17.12).
                </p>
              </div>
            </div>
          )}

          <div>
            <PaneHeader title="Project domains" />
            <div className="mt-2">
              <ChipRow
                options={domainStacks}
                selected={domains}
                onToggle={(v) => toggle(setDomains, v)}
                empty="No project domains yet — add one on the Stacks page with bucket 'project_domain'."
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              disabled={pick.isPending || !apiKey.trim() || needsPrimary}
              onClick={run}
            >
              {pick.isPending ? "Picking…" : "Pick"}
            </Button>
            {needsPrimary && (
              <span className="text-bank-muted text-xs">Choose at least one primary stack.</span>
            )}
            {result && (
              <span className="text-bank-muted text-xs">
                {result.total} questions returned{result.jd ? ` for ${result.jd.title}` : ""}
              </span>
            )}
          </div>
          <ErrorNote error={pick.error} />
        </div>
      </Panel>

      {result?.buckets.map((b) => (
        <BucketPanel key={b.bucket} bucket={b} />
      ))}
    </div>
  );
}
